import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePlan } from '@/hooks/usePlan';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireCorporate?: boolean; // Require corporate account type
  requireAccountTypes?: string[]; // Require accountType to be one of these values
  requireAccountRoles?: string[]; // Hard role check — no plan/admin bypass
  allowedRoles?: string[];    // Allowed account roles (e.g., ['admin', 'manager', 'corporate_owner'])
  requireTeamMembership?: boolean; // Require user to be member of at least one team
  checkTeamRoles?: boolean;   // Check team-level roles instead of account-level roles
}

const ProtectedRoute = ({ 
  children, 
  requireCorporate = false,
  requireAccountTypes = [],
  requireAccountRoles = [],
  allowedRoles = [],
  requireTeamMembership = false,
  checkTeamRoles = false
}: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const plan = usePlan();

  // DEBUGGING: Log route protection check
  console.log('🔒 [ProtectedRoute] Checking access:', {
    requireCorporate,
    requireTeamMembership,
    checkTeamRoles,
    allowedRoles,
    user: user ? {
      email: user.email,
      accountType: user.accountType,
      accountRole: user.accountRole,
      hasTeams: user.hasTeams,
      teamsCount: user.teams?.length || 0,
      teamRoles: user.teams?.map(t => t.role)
    } : null,
    loading
  });

  if (loading) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    console.log('🚫 [ProtectedRoute] No user found, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  // Check if team membership is required
  if (requireTeamMembership) {
    if (!user.hasTeams && (!user.teams || user.teams.length === 0)) {
      console.log('🚫 [ProtectedRoute] Team membership required but user has no teams');
      return <Navigate to="/card" replace />;
    }
    console.log('✅ [ProtectedRoute] Team membership check PASSED');
  }

  // Check if corporate account is required
  if (requireCorporate && user.accountType !== 'corporate') {
    console.log('🚫 [ProtectedRoute] Corporate required but user type is:', user.accountType);
    return <Navigate to="/card" replace />;
  }

  // Check if accountType must be one of the required types
  if (requireAccountTypes.length > 0 && !requireAccountTypes.includes(user.accountType ?? '')) {
    console.log('🚫 [ProtectedRoute] accountType not allowed:', user.accountType, '— required one of:', requireAccountTypes);
    return <Navigate to="/card" replace />;
  }

  // Hard role check — no plan/admin bypass (used for corporate dashboard)
  if (requireAccountRoles.length > 0 && !requireAccountRoles.includes(user.accountRole ?? '')) {
    console.log('🚫 [ProtectedRoute] accountRole not allowed:', user.accountRole, '— required one of:', requireAccountRoles);
    return <Navigate to="/card" replace />;
  }

  // SECURITY: Block member/viewer from team routes for corporate/business/enterprise accounts
  // UNLESS they have an active role that this route allows
  if (checkTeamRoles && ['corporate', 'business', 'enterprise'].includes(user.accountType ?? '')) {
    // Use the route's own allowedRoles to decide — so /team allows 'member', /team/:id/analytics doesn't
    const teamAllowedRoles = allowedRoles.filter(r => !['super_admin', 'corporate_owner'].includes(r));
    const hasTeamRole = user.teams?.some(team =>
      teamAllowedRoles.includes(team.role) &&
      (!team.status || team.status === 'active') // Only count active memberships
    );
    
    if (user.accountRole === 'member' && !hasTeamRole) {
      console.log('🚫 [ProtectedRoute] member without qualifying team role blocked', {
        accountType: user.accountType,
        accountRole: user.accountRole,
        teamAllowedRoles,
        teamRoles: user.teams?.map(t => ({ role: t.role, status: t.status }))
      });
      return <Navigate to="/card" replace />;
    }
    
    if (user.accountRole === 'viewer') {
      console.log('🚫 [ProtectedRoute] viewer blocked from team routes', {
        accountType: user.accountType,
        accountRole: user.accountRole
      });
      return <Navigate to="/card" replace />;
    }
  }

  // Check if specific roles are required
  if (allowedRoles.length > 0) {
    // CRITICAL: Platform admins and Business/Enterprise plan users bypass team membership checks
    // - Platform admins (corporate_owner, super_admin) have platform-level access
    // - Business/Enterprise users can create teams even if they don't have any yet
    const isPlatformAdmin = user.accountRole === 'corporate_owner' || 
                           user.accountRole === 'super_admin';
    
    const hasPlanAccess = plan.canUseTeamFeatures; // Business or Enterprise plan
    
    if (isPlatformAdmin || hasPlanAccess) {
      console.log('✅ [ProtectedRoute] Access granted - platform admin or plan allows teams', {
        isPlatformAdmin,
        hasPlanAccess,
        plan: plan.plan,
        accountRole: user.accountRole
      });
      return <>{children}</>;
    }
    
    // NEW: Check team-level roles if checkTeamRoles is true
    if (checkTeamRoles) {
      // F1/F4: Block individual/team_member users without a qualifying plan from all team routes
      if (!plan.canUseTeamFeatures && !['corporate', 'business', 'enterprise'].includes(user.accountType)) {
        console.log('🚫 [ProtectedRoute] Individual/team_member without team plan blocked', {
          accountType: user.accountType,
          plan: plan.plan
        });
        return <Navigate to="/card" replace />;
      }

      // Check if user has ANY team with an allowed role
      const hasAllowedTeamRole = user.teams?.some(team => 
        allowedRoles.includes(team.role)
      );
      
      console.log('🔍 [ProtectedRoute] Checking TEAM roles:', {
        userTeamRoles: user.teams?.map(t => t.role),
        allowedRoles,
        hasAllowedTeamRole
      });
      
      if (!hasAllowedTeamRole) {
        console.log('🚫 [ProtectedRoute] Team role check FAILED - redirecting to /card');
        return <Navigate to="/card" replace />;
      }
      
      console.log('✅ [ProtectedRoute] Team role check PASSED');
      return <>{children}</>;
    }
    
    // Original: Check profile-level accountRole
    const userRole = user.accountRole;
    console.log('🔍 [ProtectedRoute] Checking ACCOUNT role:', {
      userRole,
      allowedRoles,
      isAllowed: allowedRoles.includes(userRole)
    });
    
    // Special check for undefined/null accountRole
    if (!userRole) {
      console.error('🚨 [ProtectedRoute] CRITICAL: accountRole is undefined/null!', {
        user,
        localStorage_user_data: localStorage.getItem('user_data'),
        message: 'User object is missing accountRole. This causes redirect to /card.',
        solution: 'Logout and login again to get updated user object with accountRole'
      });
      return <Navigate to="/card" replace />;
    }
    
    if (!allowedRoles.includes(userRole)) {
      console.log('🚫 [ProtectedRoute] Account role check FAILED - redirecting to /card', {
        userRole,
        allowedRoles
      });
      return <Navigate to="/card" replace />;
    }
    
    console.log('✅ [ProtectedRoute] Account role check PASSED');
  }

  console.log('✅ [ProtectedRoute] Access granted');
  return <>{children}</>;
};

export default ProtectedRoute;